import React, { useMemo } from "react";

/* =========================================================
   DEFAULT OPTIONS
========================================================= */

const DEFAULT_OPTIONS = {
    showObjectHeaders: true,
    showFieldTypes: false,
    allowDeleteItems: true,
    allowAddItems: true,
    allowDeleteParagraphs: true,
    allowAddParagraphs: true,
    imagePreview: true,
    maxImagePreviewHeight: 180
};

/* =========================================================
   HELPERS
========================================================= */

const clone = value => {
    if (value === undefined) return undefined;

    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return value;
    }
};

const humanize = key => {
    if (!key) return "";

    return String(key)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, char =>
            char.toUpperCase()
        );
};

const isObject = value =>
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value);

const isParagraphArray = value =>
    Array.isArray(value) &&
    value.every(item =>
        typeof item === "string"
    );

const isObjectArray = value =>
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(item =>
        isObject(item)
    );

const isImageKey = key => {
    const name = String(key || "")
        .toLowerCase();

    return (
        name === "image" ||
        name === "imageurl" ||
        name === "image_url" ||
        name === "photo" ||
        name === "avatar" ||
        name === "thumbnail" ||
        name.endsWith("image")
    );
};

const isLongTextKey = key => {
    const name = String(key || "")
        .toLowerCase();

    return (
        name === "description" ||
        name === "bio" ||
        name === "content" ||
        name === "summary" ||
        name === "text" ||
        name === "body" ||
        name === "details"
    );
};

const getSchema = (schema, key) => {
    if (!schema) return {};

    return schema[key] || {};
};

const inferFieldType = (
    key,
    value,
    fieldSchema = {}
) => {
    if (fieldSchema.type) {
        return fieldSchema.type;
    }

    if (isImageKey(key)) {
        return "image";
    }

    if (isParagraphArray(value)) {
        return "paragraphs";
    }

    if (isObjectArray(value)) {
        return "items";
    }

    if (Array.isArray(value)) {
        return "array";
    }

    if (isObject(value)) {
        return "object";
    }

    if (typeof value === "boolean") {
        return "checkbox";
    }

    if (typeof value === "number") {
        return "number";
    }

    if (isLongTextKey(key)) {
        return "textarea";
    }

    return "text";
};

const createBlankValue = (
    value,
    schema = {}
) => {
    if (
        schema.default !== undefined
    ) {
        return clone(schema.default);
    }

    if (schema.type === "checkbox") {
        return false;
    }

    if (schema.type === "number") {
        return 0;
    }

    if (
        schema.type === "paragraphs" ||
        schema.type === "array"
    ) {
        return [""];
    }

    if (
        schema.type === "object" ||
        schema.type === "items"
    ) {
        return {};
    }

    if (Array.isArray(value)) {
        return [];
    }

    if (isObject(value)) {
        return {};
    }

    if (typeof value === "boolean") {
        return false;
    }

    if (typeof value === "number") {
        return 0;
    }

    return "";
};

const createItemFromTemplate = (
    template,
    schema = {}
) => {
    if (schema.itemTemplate) {
        return clone(
            schema.itemTemplate
        );
    }

    if (schema.defaultItem) {
        return clone(
            schema.defaultItem
        );
    }

    if (!template || !isObject(template)) {
        return {};
    }

    const result = {};

    Object.entries(template).forEach(
        ([key, value]) => {
            const fieldSchema =
                schema.items?.[key] ||
                schema.itemSchema?.[key] ||
                {};

            result[key] =
                createBlankValue(
                    value,
                    fieldSchema
                );
        }
    );

    return result;
};

/* =========================================================
   RECURSIVE OBJECT EDITOR

   IMPORTANT:
   This component is OUTSIDE GenericSectionEditor.
   This prevents React from remounting it on every keystroke.
========================================================= */

const GenericObjectEditor = ({
    value,
    path,
    schema = {},
    settings,
    updateValue
}) => {
    if (!isObject(value)) {
        return null;
    }

    return (
        <div
            className={
                path.length
                    ? "cms-nested-object"
                    : "cms-object"
            }
        >
            {Object.entries(value).map(
                ([key, fieldValue]) => {
                    const fieldSchema =
                        getSchema(
                            schema,
                            key
                        );

                    const fieldPath = [
                        ...path,
                        key
                    ];

                    const type =
                        inferFieldType(
                            key,
                            fieldValue,
                            fieldSchema
                        );

                    /* -------------------------
                       Nested object
                    ------------------------- */

                    if (
                        type === "object" ||
                        isObject(fieldValue)
                    ) {
                        return (
                            <div
                                className="cms-nested-section"
                                key={key}
                            >
                                {settings.showObjectHeaders && (
                                    <h4>
                                        {fieldSchema.label ||
                                            humanize(
                                                key
                                            )}
                                    </h4>
                                )}

                                <GenericObjectEditor
                                    value={
                                        fieldValue
                                    }
                                    path={
                                        fieldPath
                                    }
                                    schema={
                                        fieldSchema.fields ||
                                        fieldSchema
                                    }
                                    settings={
                                        settings
                                    }
                                    updateValue={
                                        updateValue
                                    }
                                />
                            </div>
                        );
                    }

                    /* -------------------------
                       Paragraph array
                    ------------------------- */

                    if (
                        type ===
                            "paragraphs" ||
                        isParagraphArray(
                            fieldValue
                        )
                    ) {
                        return (
                            <ParagraphEditor
                                key={key}
                                label={
                                    fieldSchema.label ||
                                    humanize(
                                        key
                                    )
                                }
                                paragraphs={
                                    fieldValue
                                }
                                path={
                                    fieldPath
                                }
                                fieldSchema={
                                    fieldSchema
                                }
                                settings={
                                    settings
                                }
                                updateValue={
                                    updateValue
                                }
                            />
                        );
                    }

                    /* -------------------------
                       Array of objects
                    ------------------------- */

                    if (
                        type === "items" ||
                        isObjectArray(
                            fieldValue
                        )
                    ) {
                        return (
                            <ItemsEditor
                                key={key}
                                label={
                                    fieldSchema.label ||
                                    humanize(
                                        key
                                    )
                                }
                                items={
                                    fieldValue
                                }
                                path={
                                    fieldPath
                                }
                                fieldSchema={
                                    fieldSchema
                                }
                                settings={
                                    settings
                                }
                                updateValue={
                                    updateValue
                                }
                            />
                        );
                    }

                    /* -------------------------
                       Generic array
                    ------------------------- */

                    if (
                        type === "array"
                    ) {
                        return (
                            <ItemsEditor
                                key={key}
                                label={
                                    fieldSchema.label ||
                                    humanize(
                                        key
                                    )
                                }
                                items={
                                    fieldValue
                                }
                                path={
                                    fieldPath
                                }
                                fieldSchema={
                                    fieldSchema
                                }
                                settings={
                                    settings
                                }
                                updateValue={
                                    updateValue
                                }
                            />
                        );
                    }

                    /* -------------------------
                       Primitive field
                    ------------------------- */

                    return (
                        <PrimitiveEditor
                            key={key}
                            fieldKey={key}
                            value={
                                fieldValue
                            }
                            path={
                                fieldPath
                            }
                            fieldSchema={
                                fieldSchema
                            }
                            settings={
                                settings
                            }
                            updateValue={
                                updateValue
                            }
                        />
                    );
                }
            )}
        </div>
    );
};

/* =========================================================
   PRIMITIVE EDITOR
========================================================= */

const PrimitiveEditor = ({
    fieldKey,
    value,
    path,
    fieldSchema,
    settings,
    updateValue
}) => {
    const type =
        inferFieldType(
            fieldKey,
            value,
            fieldSchema
        );

    const label =
        fieldSchema.label ||
        humanize(fieldKey);

    /* Checkbox */

    if (type === "checkbox") {
        return (
            <div className="cms-field">
                <label className="cms-checkbox">
                    <input
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={e =>
                            updateValue(
                                path,
                                e.target.checked
                            )
                        }
                    />

                    <span>
                        {label}
                    </span>
                </label>
            </div>
        );
    }

    /* Select */

    if (type === "select") {
        return (
            <div className="cms-field">
                <label>
                    {label}
                </label>

                <select
                    value={value ?? ""}
                    onChange={e =>
                        updateValue(
                            path,
                            e.target.value
                        )
                    }
                >
                    <option value="">
                        Select...
                    </option>

                    {(
                        fieldSchema.options ||
                        []
                    ).map(option => (
                        <option
                            key={
                                option.value
                            }
                            value={
                                option.value
                            }
                        >
                            {
                                option.label
                            }
                        </option>
                    ))}
                </select>
            </div>
        );
    }

    /* Image */

    if (type === "image") {
        return (
            <div className="cms-field cms-image-field">
                <label>
                    {label}
                </label>

                <input
                    type="text"
                    value={value ?? ""}
                    placeholder="Image URL"
                    onChange={e =>
                        updateValue(
                            path,
                            e.target.value
                        )
                    }
                />

                {settings.imagePreview &&
                    value && (
                        <div className="cms-image-preview">
                            <img
                                src={value}
                                alt={
                                    fieldSchema.alt ||
                                    `${label} preview`
                                }
                                style={{
                                    maxHeight:
                                        settings.maxImagePreviewHeight
                                }}
                                onError={e => {
                                    e.currentTarget.style.display =
                                        "none";
                                }}
                            />
                        </div>
                    )}
            </div>
        );
    }

    /* Textarea */

    if (
        type === "textarea" ||
        type === "longtext"
    ) {
        return (
            <div className="cms-field">
                <label>
                    {label}
                </label>

                <textarea
                    rows={
                        fieldSchema.rows ||
                        5
                    }
                    value={value ?? ""}
                    placeholder={
                        fieldSchema.placeholder ||
                        ""
                    }
                    onChange={e =>
                        updateValue(
                            path,
                            e.target.value
                        )
                    }
                />
            </div>
        );
    }

    /* Number */

    if (type === "number") {
        return (
            <div className="cms-field">
                <label>
                    {label}
                </label>

                <input
                    type="number"
                    value={value ?? ""}
                    onChange={e =>
                        updateValue(
                            path,
                            e.target.value ===
                                ""
                                ? ""
                                : Number(
                                    e.target
                                        .value
                                )
                        )
                    }
                />
            </div>
        );
    }

    /* Default text */

    return (
        <div className="cms-field">
            <label>
                {label}
            </label>

            <input
                type="text"
                value={value ?? ""}
                placeholder={
                    fieldSchema.placeholder ||
                    ""
                }
                onChange={e =>
                    updateValue(
                        path,
                        e.target.value
                    )
                }
            />

            {settings.showFieldTypes && (
                <small className="cms-field-type">
                    {type}
                </small>
            )}
        </div>
    );
};

/* =========================================================
   PARAGRAPH EDITOR
========================================================= */

const ParagraphEditor = ({
    label,
    paragraphs,
    path,
    fieldSchema,
    settings,
    updateValue
}) => {
    const values =
        Array.isArray(paragraphs)
            ? paragraphs
            : [];

    const updateParagraph = (
        index,
        value
    ) => {
        const updated = [
            ...values
        ];

        updated[index] = value;

        updateValue(
            path,
            updated
        );
    };

    const addParagraph = () => {
        updateValue(
            path,
            [
                ...values,
                ""
            ]
        );
    };

    const removeParagraph = index => {
        updateValue(
            path,
            values.filter(
                (_, i) =>
                    i !== index
            )
        );
    };

    return (
        <div className="cms-field cms-paragraphs-field">
            <label>
                {label}
            </label>

            {values.map(
                (
                    paragraph,
                    index
                ) => (
                    <div
                        className="cms-paragraph"
                        key={index}
                    >
                        <div className="cms-item-header">
                            <strong>
                                {label}{" "}
                                {index + 1}
                            </strong>

                            {settings.allowDeleteParagraphs && (
                                <button
                                    type="button"
                                    className="cms-delete"
                                    onClick={() =>
                                        removeParagraph(
                                            index
                                        )
                                    }
                                >
                                    Delete
                                </button>
                            )}
                        </div>

                        <textarea
                            rows={
                                fieldSchema.rows ||
                                5
                            }
                            value={
                                paragraph ??
                                ""
                            }
                            onChange={e =>
                                updateParagraph(
                                    index,
                                    e.target
                                        .value
                                )
                            }
                        />
                    </div>
                )
            )}

            {settings.allowAddParagraphs && (
                <button
                    type="button"
                    className="cms-add"
                    onClick={
                        addParagraph
                    }
                >
                    + Add Paragraph
                </button>
            )}
        </div>
    );
};

/* =========================================================
   ITEMS EDITOR
========================================================= */

const ItemsEditor = ({
    label,
    items,
    path,
    fieldSchema,
    settings,
    updateValue
}) => {
    const values =
        Array.isArray(items)
            ? items
            : [];

    const addItem = () => {
        let newItem = "";

        if (
            fieldSchema.itemTemplate
        ) {
            newItem =
                clone(
                    fieldSchema.itemTemplate
                );
        } else if (
            fieldSchema.defaultItem
        ) {
            newItem =
                clone(
                    fieldSchema.defaultItem
                );
        } else if (
            values.length > 0
        ) {
            const first =
                values[0];

            if (isObject(first)) {
                newItem =
                    createItemFromTemplate(
                        first,
                        fieldSchema
                    );
            } else {
                newItem = "";
            }
        } else if (
            fieldSchema.itemType ===
            "object"
        ) {
            newItem =
                createItemFromTemplate(
                    {},
                    fieldSchema
                );
        }

        updateValue(
            path,
            [
                ...values,
                newItem
            ]
        );
    };

    const removeItem = index => {
        updateValue(
            path,
            values.filter(
                (_, i) =>
                    i !== index
            )
        );
    };

    const updateItem = (
        index,
        value
    ) => {
        const updated = [
            ...values
        ];

        updated[index] = value;

        updateValue(
            path,
            updated
        );
    };

    return (
        <div className="cms-items">
            <div className="cms-items-title">
                <h4>{label}</h4>
            </div>

            {values.map(
                (
                    item,
                    index
                ) => (
                    <div
                        className="cms-item"
                        key={index}
                    >
                        <div className="cms-item-header">
                            <strong>
                                {label}{" "}
                                {index + 1}
                            </strong>

                            {settings.allowDeleteItems && (
                                <button
                                    type="button"
                                    className="cms-delete"
                                    onClick={() =>
                                        removeItem(
                                            index
                                        )
                                    }
                                >
                                    Delete Item
                                </button>
                            )}
                        </div>

                        {isObject(item) ? (
                            <GenericObjectEditor
                                value={item}
                                path={[]}
                                schema={
                                    fieldSchema.items ||
                                    fieldSchema.itemSchema ||
                                    {}
                                }
                                settings={
                                    settings
                                }
                                updateValue={(
                                    itemPath,
                                    itemValue
                                ) => {
                                    if (
                                        itemPath.length ===
                                        0
                                    ) {
                                        updateItem(
                                            index,
                                            itemValue
                                        );
                                        return;
                                    }

                                    const updateNested =
                                        (
                                            current,
                                            pIndex
                                        ) => {
                                            if (
                                                pIndex ===
                                                itemPath.length
                                            ) {
                                                return itemValue;
                                            }

                                            const key =
                                                itemPath[
                                                    pIndex
                                                ];

                                            return {
                                                ...(current ||
                                                    {}),
                                                [key]:
                                                    updateNested(
                                                        current?.[
                                                            key
                                                        ],
                                                        pIndex +
                                                            1
                                                    )
                                            };
                                        };

                                    updateItem(
                                        index,
                                        updateNested(
                                            item,
                                            0
                                        )
                                    );
                                }}
                            />
                        ) : (
                            <PrimitiveEditor
                                fieldKey={`${label}-${index}`}
                                value={item}
                                path={[]}
                                fieldSchema={
                                    fieldSchema
                                }
                                settings={
                                    settings
                                }
                                updateValue={(
                                    _,
                                    newValue
                                ) =>
                                    updateItem(
                                        index,
                                        newValue
                                    )
                                }
                            />
                        )}
                    </div>
                )
            )}

            {settings.allowAddItems && (
                <button
                    type="button"
                    className="cms-add"
                    onClick={addItem}
                >
                    + Add Item
                </button>
            )}
        </div>
    );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const GenericSectionEditor = ({
    data = {},
    update,
    schema = {},
    options = {}
}) => {
    const settings = useMemo(
        () => ({
            ...DEFAULT_OPTIONS,
            ...options
        }),
        [options]
    );

    /*
     * IMPORTANT:
     *
     * This function creates a NEW data object,
     * but it does NOT create a new component type.
     *
     * Therefore input elements keep their focus.
     */

    const updateValue = (
        path,
        value
    ) => {
        if (!path || !path.length) {
            return;
        }

        const rootKey = path[0];

        if (path.length === 1) {
            update(
                rootKey,
                value
            );
            return;
        }

        const setNested = (
            current,
            index
        ) => {
            if (
                index ===
                path.length
            ) {
                return value;
            }

            const key =
                path[index];

            if (
                Array.isArray(
                    current
                )
            ) {
                const result = [
                    ...current
                ];

                result[key] =
                    setNested(
                        current[key],
                        index + 1
                    );

                return result;
            }

            return {
                ...(current || {}),
                [key]: setNested(
                    current?.[key],
                    index + 1
                )
            };
        };

        const newRootValue =
            setNested(
                data[rootKey],
                1
            );

        update(
            rootKey,
            newRootValue
        );
    };

    return (
        <div className="cms-editor cms-generic-editor">
            <GenericObjectEditor
                value={data}
                path={[]}
                schema={schema}
                settings={settings}
                updateValue={
                    updateValue
                }
            />
        </div>
    );
};

export default GenericSectionEditor;